import { storeWrapper } from '../storeWrapper'
import { useAuthenticationStore } from '@repo/store/useAuthenticationStore'
import { debounce } from '@repo/utils/debounce'
import { ospreyAPI } from '@repo/api/ospreyAPI'

const initialState = {
  allNotifications: [],
  newNotification: null,
  notificationDrawerIsOpen: false,
  notificationTypeId: null,
  notificationSourceId: null,
  notificationId: null,
  isNotificationWizard: false,
  isNewClient: false,
  coisSelect: true
}

export const useNotificationStore = storeWrapper('useNotificationStore', (set, get) => ({
  ...initialState,
  setNotificationId: (id) => {
    set({ notificationId: id })
  },
  setIsNotificationWizard: (bool) => {
    set({ isNotificationWizard: bool })
  },
  fetchAllNotifications: async ({ notificationStatusIds, startAtIndex, pageSize }) => {
    return new Promise((resolve) => {
      debounce({
        id: 'fetchAllNotifications',
        delay: 500,
        callback: async () => {
          let fetchedAllNotifications
          try {
            fetchedAllNotifications = await ospreyAPI({
              method: 'GET',
              service: 'notifications',
              endpoint: 'Notification/GetAllExtendedByCurrentUser',
              data: {
                notificationTypeId: get().notificationTypeId,
                notificationSourceId: get().notificationSourceId,
                notificationStatusIds: notificationStatusIds,
                startAtIndex: startAtIndex ?? 0,
                pageSize: pageSize ?? 25
              }
            })
          } catch (error) {
            console.error('Error fetching all notifications:', error)
          }

          set({ allNotifications: fetchedAllNotifications })
          resolve(fetchedAllNotifications)
        }
      })
    })
  },
  updateNotificationStatus: async ({ notificationId, notificationStatusId }) => {
    return new Promise((resolve) => {
      debounce({
        id: 'updateNotificationStatus',
        delay: 500,
        callback: async () => {
          let updatedNotificationStatus
          try {
            updatedNotificationStatus = await ospreyAPI({
              method: 'PUT',
              service: 'notifications',
              endpoint: 'Notification/UpdateStatus',
              data: {
                notificationId: notificationId,
                notificationStatusId: notificationStatusId
              },
              sendAsUrlParams: true
            })
          } catch (error) {
            console.error('Error updating notification status:', error)
          }

          resolve(updatedNotificationStatus)
        }
      })
    })
  },
  fetchNewMatterNotification: async (notificationId) => {
    return new Promise((resolve) => {
      debounce({
        id: 'fetchNewMatterNotification',
        delay: 500,
        callback: async () => {
          let fetchedNewMatterNotification
          try {
            fetchedNewMatterNotification = await ospreyAPI({
              method: 'GET',
              service: 'notifications',
              endpoint: 'Notification/GetNewMatterNotification',
              data: {
                notificationId: notificationId ?? get().notificationId
              }
            })
          } catch (error) {
            console.error('Error fetching new matter notification:', error)
          }

          set({ newNotification: fetchedNewMatterNotification })
          resolve(fetchedNewMatterNotification)
        }
      })
    })
  },
  lockNotification: (notificationId) => {
    const notification = get().allNotifications?.rows.find((n) => n.notificationId === notificationId)
    const { userId } = useAuthenticationStore.getState()
    const isLockedToCurrentUser = notification?.lockedByUserId
      ? notification.lockedByUserId.toLowerCase() === userId.toLowerCase()
      : false
    if (isLockedToCurrentUser) return

    return new Promise((resolve) => {
      debounce({
        id: `lockNotification-${notificationId ?? get().notificationId}`,
        delay: 500,
        callback: async () => {
          let updatedLockedStatus
          try {
            updatedLockedStatus = await ospreyAPI({
              method: 'PUT',
              service: 'notifications',
              endpoint: 'Notification/Lock',
              data: {
                notificationId: notificationId ?? get().notificationId
              },
              sendAsUrlParams: true
            })
          } catch (error) {
            console.error('Error setting notification locked status:', error)
          }

          resolve(updatedLockedStatus)
        }
      })
    })
  },
  unlockNotification: (notificationId) => {
    return new Promise((resolve) => {
      debounce({
        id: `unlockNotification-${notificationId ?? get().notificationId}`,
        delay: 500,
        callback: async () => {
          let updatedLockedStatus
          try {
            updatedLockedStatus = await ospreyAPI({
              method: 'PUT',
              service: 'notifications',
              endpoint: 'Notification/Unlock',
              data: {
                notificationId: notificationId ?? get().notificationId
              },
              sendAsUrlParams: true
            })
          } catch (error) {
            console.error('Error setting notification unlocked status:', error)
          }

          resolve(updatedLockedStatus)
        }
      })
    })
  },
  completeNewMatterNotification: async ({ clientNo, matterNo }) => {
    return new Promise((resolve) => {
      debounce({
        id: `completeNewMatterNotification-${get().notificationId}`,
        delay: 500,
        callback: async () => {
          let completedNewMatterNotification
          try {
            completedNewMatterNotification = await ospreyAPI({
              method: 'PUT',
              service: 'notifications',
              endpoint: 'Notification/CompleteNewMatterNotification',
              data: {
                notificationId: get().notificationId,
                clientNo: clientNo,
                matterNo: matterNo
              },
              sendAsUrlParams: true
            })
          } catch (error) {
            console.error('Error completing new matter notification:', error)
          }

          resolve(completedNewMatterNotification)
        }
      })
    })
  },
  setNotificationDrawerIsOpen: (bool) => {
    set({ notificationDrawerIsOpen: bool })
  },
  setNotificationTypeId: (id) => {
    set({ notificationTypeId: id })
  },
  setNotificationSourceId: (id) => {
    set({ notificationSourceId: id })
  },
  setIsNewClient: (bool) => {
    set({ isNewClient: bool })
  },
  setCOISSelect: (bool) => {
    set({ coisSelect: bool })
  },
  reset: () => set(initialState)
}))
